import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lock, Users, Trophy, ChevronRight, Star, UserPlus } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assets";

export interface LeagueCardProps {
  id: string;
  name: string;
  imageUrl?: string;
  memberCount: number;
  isPrivate: boolean;
  creatorName: string;
  isFavorite?: boolean;
  pendingJoinRequestsCount?: number;
  onView?: (id: string) => void;
  onToggleFavorite?: (id: string) => void;
}

export function LeagueCard({
  id,
  name,
  imageUrl,
  memberCount,
  isPrivate,
  creatorName,
  isFavorite = false,
  pendingJoinRequestsCount = 0,
  onView,
  onToggleFavorite,
}: LeagueCardProps) {
  const showPendingBadge = pendingJoinRequestsCount > 0;
  const pendingLabel =
    pendingJoinRequestsCount === 1
      ? "1 solicitud pendiente"
      : `${pendingJoinRequestsCount} solicitudes pendientes`;
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(id);
  };

  return (
    <Card 
      className="hover-elevate active-elevate-2 overflow-visible cursor-pointer" 
      data-testid={`card-league-${id}`}
      onClick={() => onView?.(id)}
    >
      <div className="flex items-center gap-3 p-3">
        {imageUrl ? (
          <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0">
            <img
              src={resolveAssetUrl(imageUrl)}
              alt={name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Trophy className="h-6 w-6 text-primary" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isPrivate && <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}
            <p className="font-semibold truncate">{name}</p>
            {showPendingBadge && (
              <Badge
                variant="default"
                className="gap-1 px-1.5 flex-shrink-0"
                aria-label={pendingLabel}
                data-testid={`badge-pending-requests-${id}`}
              >
                <UserPlus className="h-3 w-3" />
                {pendingJoinRequestsCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              {memberCount}
            </span>
            <span className="text-xs text-muted-foreground">•</span>
            <Badge variant="outline" className="text-xs py-0 h-5">
              {creatorName}
            </Badge>
          </div>
        </div>
        
        <button
          onClick={handleFavoriteClick}
          className="p-1.5 rounded-full hover:bg-muted transition-colors flex-shrink-0"
          data-testid={`button-favorite-league-${id}`}
          aria-label={isFavorite ? "Quitar de favoritos" : "Añadir a favoritos"}
        >
          <Star 
            className={`h-5 w-5 ${isFavorite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} 
          />
        </button>
        
        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      </div>
    </Card>
  );
}
